import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Clock, Lock, ShieldCheck, Trash2 } from 'lucide-react';
import { ACTION_LABELS, ACTION_REASONS, VERIFICATION_TYPES, canDo, nextStep, stepLink, stepsFor } from '@/utils/verifications';
import VerificationSteps, { useIdentityDetail } from './VerificationSteps';
import '@/styles/verification.css';

function progressOf(steps) {
  const done = steps.filter((s) => s.state === 'VERIFIED').length;
  return { done, total: steps.length, pct: steps.length ? Math.round((done / steps.length) * 100) : 0 };
}

function Progress({ steps }) {
  const { done, total, pct } = progressOf(steps);
  return (
    <>
      <div className="vg-progress" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={done}
        aria-label="Étapes de vérification faites">
        <div className="vg-progress-bar" style={{ width: `${pct}%` }} />
      </div>
      <div className="vg-progress-label">{done} étape{done > 1 ? 's' : ''} sur {total}</div>
    </>
  );
}

function PageGate({ user, action, from, goal }) {
  const steps = stepsFor(user, action);
  const identity = useIdentityDetail(steps);
  const label = goal || ACTION_LABELS[action] || ACTION_LABELS.publish;
  const remaining = steps.filter((s) => s.state !== 'VERIFIED').length;
  const waiting = !nextStep(steps);
  const title = waiting
    ? 'Votre compte est en cours de vérification'
    : remaining === 1 ? `Plus qu’une étape pour ${label}` : `Plus que ${remaining} étapes pour ${label}`;
  const reason = waiting
    ? 'Nous vous prévenons par e-mail dès que c’est fait — inutile de renvoyer vos documents.'
    : ACTION_REASONS[action];
  const identityTodo = steps.some((s) => s.kind === 'identity' && s.state !== 'VERIFIED');
  return (
    <section className="vg vg-page" role="status" data-testid="verification-required" data-variant="page">
      <div className="vg-head">
        <span className={`vg-head-icon${waiting ? ' vg-head-icon--wait' : ''}`}>
          {waiting ? <Clock size={22} aria-hidden="true" /> : <ShieldCheck size={22} aria-hidden="true" />}
        </span>
        <div>
          <h2 className="vg-title">{title}</h2>
          {reason && <p className="vg-reason">{reason}</p>}
        </div>
      </div>
      <Progress steps={steps} />
      <VerificationSteps steps={steps} from={from} identity={identity} />
      {identityTodo && (
        <div className="vg-trust">
          <span><Lock size={13} aria-hidden="true" /> Pièce d’identité chiffrée, vue par notre équipe seulement</span>
          <span><Trash2 size={13} aria-hidden="true" /> Supprimée 30 jours après la décision</span>
        </div>
      )}
    </section>
  );
}

function PanelGate({ user, action, from, goal }) {
  const steps = stepsFor(user, action);
  const next = nextStep(steps);
  const rest = steps.filter((s) => s.state !== 'VERIFIED');
  const label = goal || ACTION_LABELS[action] || ACTION_LABELS.publish;
  return (
    <section className="vg vg-panel" role="status" data-testid="verification-required" data-variant="panel">
      <div className="vg-panel-title"><Lock size={16} aria-hidden="true" /> Pour {label}, vérifiez votre compte</div>
      <Progress steps={steps} />
      <div className="vg-panel-rest">
        {rest.map((s) => (
          <span key={s.kind} className={`vg-chip vg-chip--${s.state}`} data-testid={`verification-missing-${s.kind}`}>
            {VERIFICATION_TYPES[s.kind]?.label || s.kind}{s.state === 'PENDING' ? ' · en cours d’examen' : ''}
          </span>
        ))}
      </div>
      {next ? (
        <>
          <Link to={stepLink(next.kind, from)} className="vg-btn vg-btn--primary" data-testid={`verification-cta-${next.kind}`}>
            {next.state === 'REJECTED' ? 'Renvoyer ma pièce' : VERIFICATION_TYPES[next.kind]?.cta || 'Vérifier'}
          </Link>
          <div className="vg-note">Vous reviendrez ici ensuite.</div>
        </>
      ) : (
        <div className="vg-note">Votre pièce d’identité est en cours d’examen (réponse sous 24 h). Vous serez prévenu par e-mail.</div>
      )}
    </section>
  );
}

/**
 * Ce qui remplace une action tant que le compte n'est pas vérifié pour elle (rien si elle est permise).
 *  - `variant="page"` : l'action est la page (publier, versements) — titre, progression, toutes les étapes ;
 *  - `variant="panel"` : l'action est une partie de la page (proposer, contacter, PRO) — progression, prochaine étape.
 * Les étapes viennent du serveur (`stepsFor`) ; chaque lien ramène à la page courante (`?retour=`). `goal` précise
 * l'action pour l'écran (« publier un trajet ») ; sinon, le libellé général de l'action.
 */
export default function VerificationGate({ user, action = 'publish', variant = 'page', goal }) {
  const location = useLocation();
  if (!user || canDo(user, action)) return null;
  const from = `${location.pathname}${location.search || ''}`;
  return variant === 'panel'
    ? <PanelGate user={user} action={action} from={from} goal={goal} />
    : <PageGate user={user} action={action} from={from} goal={goal} />;
}
